/** @format */

import * as lodash from 'lodash-es';
import moment from 'moment';
import * as rxjs from 'rxjs';

import { Workspace } from '@shared/constants/habilitation/workspace';
import { WorkspaceModule, WorkspaceModulesMap } from '@shared/constants/habilitation/workspace-module';
import { CustomerAccount } from '../models/customer-account/customer-account.class';
import { Organization } from '../models/organization/organization.class';
import { User } from '../models/user/user.class';

export type GlobalModule = 'dashboard' | 'help';
export type AppModule = WorkspaceModule | GlobalModule;

export interface IPendingFieldCheckpointScan {
  scanId: string;
  token: string;
  equipmentId: string;
  userId: string;
  scannedAt: number;
  source: 'qr' | 'nfc';
  canEndOperation: boolean;
  recordedOperationId: string | null;
}

export interface ISessionData {
  $amplifyReady: rxjs.BehaviorSubject<boolean>;
  $maintenance: rxjs.BehaviorSubject<boolean>;
  $locale: rxjs.BehaviorSubject<string>;
  $decimalSymbol: rxjs.BehaviorSubject<string>;

  $loaded: rxjs.BehaviorSubject<boolean>;

  $customerAccounts: rxjs.BehaviorSubject<CustomerAccount[] | null>;
  $customerAccount: rxjs.BehaviorSubject<CustomerAccount | null>;

  $organization: rxjs.BehaviorSubject<Organization | null>;

  $workspace: rxjs.BehaviorSubject<Workspace | null>;
  $module: rxjs.BehaviorSubject<AppModule | null>;

  $user: rxjs.BehaviorSubject<User | null>;

  $logAs: rxjs.BehaviorSubject<string | null>;
  $pendingFieldCheckpointScan: rxjs.BehaviorSubject<IPendingFieldCheckpointScan | null>;
}

export const SessionData: ISessionData = {
  $amplifyReady: new rxjs.BehaviorSubject(false),
  $maintenance: new rxjs.BehaviorSubject(false),
  $locale: new rxjs.BehaviorSubject('fr'),
  $decimalSymbol: new rxjs.BehaviorSubject(','),

  $loaded: new rxjs.BehaviorSubject(false),

  $customerAccounts: new rxjs.BehaviorSubject<CustomerAccount[] | null>(null),
  $customerAccount: new rxjs.BehaviorSubject<CustomerAccount | null>(null),

  $organization: new rxjs.BehaviorSubject<Organization | null>(null),

  $workspace: new rxjs.BehaviorSubject<Workspace | null>(null),
  $module: new rxjs.BehaviorSubject<AppModule | null>(null),

  $user: new rxjs.BehaviorSubject<User | null>(null),

  $logAs: new rxjs.BehaviorSubject<string | null>(null),
  $pendingFieldCheckpointScan: new rxjs.BehaviorSubject<IPendingFieldCheckpointScan | null>(null),
};

/* ---------------------------------------------------------------
 * Automatic reactive dependencies
 * -------------------------------------------------------------*/

// Keep locale synced with moment
SessionData.$locale.subscribe(locale => moment.locale(locale));

// Reset module when workspace changes or when current module is invalid
SessionData.$workspace.subscribe(workspace => {
  const currentModule = SessionData.$module.value;
  if (!workspace) {
    SessionData.$module.next(null);
    return;
  }
  if (!currentModule || !lodash.includes(WorkspaceModulesMap[workspace], currentModule)) {
    SessionData.$module.next(null);
  }
});

// Organization automatically updates customerAccount
SessionData.$organization.subscribe(organization => {
  if (!organization) return;

  const orgCustomerId = organization.customerAccount?._id;
  const currentCustomerId = SessionData.$customerAccount.value?._id;

  if (orgCustomerId && orgCustomerId !== currentCustomerId) {
    const accounts = SessionData.$customerAccounts.value ?? [];
    const found = accounts.find(ca => ca._id === orgCustomerId) ?? null;
    SessionData.$customerAccount.next(found);
  }
});
